import React, { useEffect } from 'react';
import { useCombobox } from 'downshift';
import cx from 'classnames';
import { getSearchSuggestions } from 'clientAPI/searchsuggestions';
import { useRevalidator } from 'react-router-dom';
import { Icon, Label } from '@nike/eds';
import sx from './SearchTermComboBox.module.css';
import { useDebouncedCallback } from 'use-debounce';
import { useQueryClient } from '@tanstack/react-query';
import { QUERY_KEYS } from '../../constants';

type SearchTermComboBoxProps = {
  onValueChange: (v: string) => void;
  language: string;
  marketplace: string;
  suggestionFlow: string; // 'old' | 'new';
  autocomplete?: boolean; // default to true
  defaultSearchText: string;
};

export function SearchTermComboBox(props: SearchTermComboBoxProps) {
  const { language, marketplace, suggestionFlow, defaultSearchText } = props;
  const autocomplete = 'autocomplete' in props ? props.autocomplete : true;
  const [items, setItems] = React.useState<Array<SearchSuggestItem>>([]);
  const revalidator = useRevalidator();
  const queryClient = useQueryClient();
  const updateOptions = useDebouncedCallback((v: string) => {
    const qs = new URLSearchParams({
      country: marketplace,
      language,
      text: v,
      count: '20',
      searchOptions: `suggestionFlow:${suggestionFlow}`,
    });
    queryClient
      .fetchQuery({
        queryKey: [QUERY_KEYS.SEARCH_SUGGESTIONS, qs.toString()],
        queryFn: getSearchSuggestions(),
        staleTime: 30000,
      })
      .then((d) => setItems(d.searchTerms))
      .catch((err: keyable) => err.status === 401 && revalidator.revalidate());
  }, 200);
  const {
    isOpen,
    getLabelProps,
    getMenuProps,
    getInputProps,
    highlightedIndex,
    getItemProps,
    selectedItem,
  } = useCombobox({
    onInputValueChange(change) {
      if (typeof change.inputValue !== 'string') return;
      props.onValueChange(change.inputValue);
      if (autocomplete) updateOptions(change.inputValue);
    },
    items,
    itemToString(item) {
      return item ? item.searchText : '';
    },
    initialInputValue: defaultSearchText,
  });
  return (
    <div className='tw-relative'>
      <div className='tw-flex tw-flex-col'>
        <Label
          font='title-6'
          htmlFor='this-makes-eslint-happy-and-will-be-overwritten'
          {...getLabelProps()}
        >
          Search Terms
        </Label>

        <div className={cx('tw-flex', sx.inputContainer)}>
          <div
            className='tw-inline-flex tw-justify-center tw-items-center tw-pr-2'
            style={{ color: 'var(--eds-color-grey-1)' }}
          >
            <Icon name='Search' />
          </div>
          <input
            placeholder='Enter Text'
            className={cx('tw-w-full', sx.input)}
            {...getInputProps()}
          />
        </div>
      </div>
      <ul
        className={cx(
          'tw-absolute tw-left-0 tw-right-0 tw-bg-white tw-mt-1 tw-shadow-md tw-max-h-96 tw-overflow-scroll tw-p-0 tw-z-10',
          {
            'tw-hidden': !(isOpen && items.length),
          },
          sx.optionList,
        )}
        {...getMenuProps()}
      >
        {isOpen &&
          items.map((item, index) => (
            <li
              className={cx(
                sx.option,
                highlightedIndex === index && sx.active,
                selectedItem === item && 'tw-font-bold',
                'tw-py-2 tw-px-3 tw-shadow-sm tw-flex tw-flex-col',
              )}
              key={item.id}
              {...getItemProps({ item, index })}
            >
              <span>{item.displayText}</span>
            </li>
          ))}
      </ul>
    </div>
  );
}
